import { Cell } from '../grid/Cell'
import { BaseModal } from './BaseModal'
import { Shape } from '../puzzle/Shape'
import { Shape as ShapeData } from '../../lib/shapes'
import { getPuzzle } from '../../lib/words'
import { PlayIcon, AcademicCapIcon, LightBulbIcon } from '@heroicons/react/outline'
import { useNavigate } from 'react-router-dom'
import CursorExampleImage from '../../cursor_example.png'
import MultiCursorImage from '../../multicursor.png'

type Props = {
  isOpen: boolean
  isDarkMode: boolean
  handleClose: () => void
}

export const InfoModal = ({ isOpen, isDarkMode, handleClose }: Props) => {
  const navigate = useNavigate()
	const example = 'people'
  const puzzle = getPuzzle(example)
	const animationClasses = new Map([['p', 'flip-1'],
																	 ['e', 'flip-2'],
																	 ['o', 'flip-3'],
																	 ['l', 'flip-4']])

  const openTips = () => {
    handleClose()
    navigate('/tips')
  }

	const flipCard = (key: number, l: string, s: ShapeData) => {
    return (<div key={key} className="flip-card h-12 w-12 sm:h-14 sm:w-14">
    	<div className={`flip-card-inner h-12 w-12 sm:h-14 sm:w-14 ${animationClasses.get(l)}`}>
      	<div className="flip-card-front h-12 w-12 sm:h-14 sm:w-14">
      	  <Shape shape={s} isDarkMode={isDarkMode} useMargin={true} />
      	</div>
        <div className="flip-card-back flex justify-center items-center
          h-12 w-12 sm:h-14 sm:w-14 text-3xl sm:text-4xl font-bold rounded text-gray-700 dark:text-gray-300 uppercase">
      	  {l}
      	</div>
    	</div>
    </div>)
	}

  return (
    <BaseModal title="How to play" isOpen={isOpen} handleClose={handleClose}>
      <p className="text-sm text-gray-700 dark:text-gray-300 p-2">
        Guess the word in 6 tries.
      </p>
      <p className="text-sm text-gray-700 dark:text-gray-300 p-2">
        Each symbol represents a letter in the word.
      </p>
    	<div className="flex justify-center m-4" role="list" aria-label="puzzle">
        {['p', 'e', 'o', 'p', 'l', 'e'].map((l: string, i:number) => flipCard(i, l, puzzle[i]))}
			</div>
      <a href="https://www.tiktok.com/@wordletips/video/7141108502038105387?is_from_webapp=v1&item_id=7141108502038105387" target="_blank" rel="noopener noreferrer" className="flex justify-center block text-sm text-gray-700 dark:text-gray-300 p-2 hover:text-indigo-700 font-bold">
        <PlayIcon className="inline-flex h-6 w-6 scale-up cursor-pointer dark:stroke-white mr-1" />
        Watch an example solve
      </a>
      <p className="text-sm text-gray-700 dark:text-gray-300 p-2">
        You may guess any word of the correct length. Tiles will reveal if your letters are in the secret word.
      </p>
      <div className="grid grid-cols-4 gap-4 m-8 max-w-sm">
        <div className="col-span-1 flex items-center justify-center">
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="P"
            status="correct"
          />
        </div>
        <div className="col-span-3 flex items-center justify-center">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Letter is in the correct location
          </p>
        </div>
        <div className="col-span-1 flex items-center justify-center">
          <Cell value="E"
            isRevealing={true}
            isCompleted={true}
            status="present"
          />
        </div>
        <div className="col-span-3 flex items-center justify-center">
          <p className="text-sm text-gray-700 dark:text-gray-300 col-span-3">
            Letter is in the word but in a different location
          </p>
        </div>
        <div className="col-span-1 flex items-center justify-center">
          <Cell isRevealing={true} isCompleted={true} value="I" status="absent" />
        </div>
        <div className="col-span-3 flex items-center justify-center">
          <p className="text-sm text-gray-700 dark:text-gray-300 col-span-3">
            Letter is not in the word
          </p>
        </div>
      </div>
      <h3 className="title text-lg my-4 font-bold dark:text-gray-300">Stuck?</h3>
      <p className="italic p-2 text-gray-700 dark:text-gray-300">Try clicking the <LightBulbIcon className="inline-flex h-6 w-6 mr-1" />
        Icon in the keyboard for some ideas.</p>
      <h3 className="title text-lg my-4 font-bold dark:text-gray-300">The cursor</h3>
      <div className="flex flex-col items-center justify-center">
        <img className="m-4 w-96 border-4" src={CursorExampleImage} alt="cursor example" />
        <p className="text-sm text-gray-700 dark:text-gray-300 p-2">Polygonle features a character cursor for visualizing words without filling every letter. Click a tile to fill it and navigate the cursor with spacebar or arrow keys instead of typing.</p>
        <p className="text-sm text-gray-700 dark:text-gray-300 p-2">
        Hold [shift] to fill every square which matches a shape or enable multi cursor in the settings menu.</p>
      </div>
      <div className="flex flex-col items-center justify-center">
        <img className="my-2 w-96 border-4" src={MultiCursorImage} alt="highcontrast" />
      </div>
      <div className="flex flex-col justify-center items-center m-8">
      <div className="italic text-gray-700 dark:text-gray-300"> Need more solve tips? </div>
        <button
          onClick={openTips}
          className="text-gray-900 mt-2 text-white group flex w-64 items-center rounded-md px-4 py-2 title text-lg justify-center items-center text-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm row-span-1">
          <AcademicCapIcon 
            className="h-6 w-6 mr-2 scale-up cursor-pointer stroke-white"
            aria-label={'tips'}
            role="button"
            tabIndex={0}
          />
          <div className="text-sm text-white">Tips & Tricks</div>
        </button>
      </div>
      <div className="mt-6 italic text-sm text-gray-700 dark:text-gray-300">
      	Based on the open source {' '} <a
          href="https://github.com/cwackerfuss/react-wordle"
          className="underline font-bold"
        >react-wordle
        </a>{' '}
       	project.
      </div>
    </BaseModal>
		)
}
